import { useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import Navbar from "@/components/Navbar";
import { Footer } from "@/components/sections/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { 
  Megaphone, 
  Image as ImageIcon, 
  Upload, 
  Eye, 
  Users, 
  TrendingUp,
  CheckCircle2,
  Loader2
} from "lucide-react";

const AD_PACKAGES = {
  banner: {
    price: 500,
    duration: 30,
    name_en: "Banner Ad",
    name_ar: "إعلان بانر",
    description_en: "Top of page banner visible on all pages",
    description_ar: "بانر أعلى الصفحة يظهر في جميع الصفحات",
    features_en: ["Maximum visibility", "Auto-rotating display", "30 days exposure", "Limited time offer!"],
    features_ar: ["أقصى وضوح", "عرض متناوب تلقائي", "تعرض لمدة 30 يوم", "عرض لفترة محدودة!"],
    isLimited: true
  },
  sidebar: {
    price: 300,
    duration: 30,
    name_en: "Sidebar Ad",
    name_ar: "إعلان جانبي",
    description_en: "Sidebar placement on event pages",
    description_ar: "وضع جانبي في صفحات الفعاليات",
    features_en: ["Targeted placement", "Event detail pages", "30 days exposure", "Limited time offer!"],
    features_ar: ["وضع مستهدف", "صفحات تفاصيل الفعاليات", "تعرض لمدة 30 يوم", "عرض لفترة محدودة!"],
    isLimited: true
  }
};

export default function Advertise() {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === "ar";
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const [selectedPackage, setSelectedPackage] = useState<"banner" | "sidebar">("banner");
  const [isLoading, setIsLoading] = useState(false);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  
  const [formData, setFormData] = useState({
    advertiser_name: "",
    advertiser_email: "",
    advertiser_phone: "",
    title: "",
    title_ar: "",
    description: "",
    description_ar: "",
    link_url: ""
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error(isRTL ? "حجم الصورة يجب أن يكون أقل من 5 ميجابايت" : "Image must be less than 5MB");
      return;
    }

    // Preview
    const reader = new FileReader();
    reader.onload = (e) => setImagePreview(e.target?.result as string);
    reader.readAsDataURL(file);

    // Upload to Supabase
    const fileExt = file.name.split('.').pop();
    const fileName = `ad-${Date.now()}.${fileExt}`;

    const { data, error } = await supabase.storage
      .from("ad-images")
      .upload(fileName, file);

    if (error) {
      toast.error(isRTL ? "فشل رفع الصورة" : "Failed to upload image");
      return;
    }

    const { data: { publicUrl } } = supabase.storage
      .from("ad-images")
      .getPublicUrl(fileName);

    setImageUrl(publicUrl);
    toast.success(isRTL ? "تم رفع الصورة بنجاح" : "Image uploaded successfully");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validation
    if (!formData.advertiser_name || !formData.advertiser_email || !formData.title || !formData.link_url || !imageUrl) {
      toast.error(isRTL ? "يرجى ملء جميع الحقول المطلوبة" : "Please fill all required fields");
      return;
    }

    setIsLoading(true);

    try {
      const { data, error } = await supabase.functions.invoke("create-paymob-ad-payment", {
        body: {
          adType: selectedPackage,
          adData: {
            ...formData,
            image_url: imageUrl
          }
        }
      });

      if (error) throw error;

      if (data?.url) {
        window.open(data.url, "_blank");
      }
    } catch (error: any) {
      console.error("Payment error:", error);
      toast.error(isRTL ? "حدث خطأ أثناء إنشاء الدفع" : "Error creating payment");
    } finally {
      setIsLoading(false);
    }
  };

  const pkg = AD_PACKAGES[selectedPackage];

  return (
    <>
      <Helmet>
        <title>{isRTL ? "أعلن معنا | منصة الفعاليات" : "Advertise With Us | Events Platform"}</title>
        <meta 
          name="description" 
          content={isRTL ? "اعرض إعلانك أمام آلاف الزوار المهتمين بالفعاليات" : "Display your ad to thousands of event-interested visitors"} 
        />
      </Helmet>

      <div className="min-h-screen bg-background" dir={isRTL ? "rtl" : "ltr"}>
        <Navbar />
        
        {/* Hero Section */}
        <section className="relative py-20 bg-gradient-to-br from-primary/10 via-background to-accent/10">
          <div className="container mx-auto px-4 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="max-w-3xl mx-auto"
            >
              <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full mb-6">
                <Megaphone className="w-5 h-5" />
                <span className="font-medium">
                  {isRTL ? "منصة الإعلانات" : "Advertising Platform"}
                </span>
              </div>
              
              <h1 className="text-4xl md:text-5xl font-bold mb-6">
                {isRTL ? "أعلن معنا وصل لجمهورك" : "Advertise With Us & Reach Your Audience"}
              </h1>
              
              <p className="text-xl text-muted-foreground mb-8">
                {isRTL 
                  ? "اعرض إعلانك أمام آلاف الزوار المهتمين بالفعاليات والأنشطة"
                  : "Display your ad to thousands of visitors interested in events and activities"
                }
              </p>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-6 max-w-lg mx-auto">
                <div className="text-center">
                  <Eye className="w-8 h-8 mx-auto mb-2 text-primary" />
                  <div className="text-2xl font-bold">50K+</div>
                  <div className="text-sm text-muted-foreground">
                    {isRTL ? "مشاهدة شهرية" : "Monthly Views"}
                  </div>
                </div>
                <div className="text-center">
                  <Users className="w-8 h-8 mx-auto mb-2 text-primary" />
                  <div className="text-2xl font-bold">10K+</div>
                  <div className="text-sm text-muted-foreground">
                    {isRTL ? "زائر فريد" : "Unique Visitors"}
                  </div>
                </div>
                <div className="text-center">
                  <TrendingUp className="w-8 h-8 mx-auto mb-2 text-primary" />
                  <div className="text-2xl font-bold">5%+</div>
                  <div className="text-sm text-muted-foreground">
                    {isRTL ? "معدل النقر" : "Click Rate"}
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Main Content */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="grid lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
              
              {/* Package Selection */}
              <div>
                <h2 className="text-2xl font-bold mb-6">
                  {isRTL ? "اختر باقة الإعلان" : "Choose Ad Package"}
                </h2>
                
                <RadioGroup
                  value={selectedPackage}
                  onValueChange={(v) => setSelectedPackage(v as "banner" | "sidebar")}
                  className="space-y-4"
                >
                  {(Object.entries(AD_PACKAGES) as [keyof typeof AD_PACKAGES, typeof AD_PACKAGES.banner][]).map(([key, pkg]) => (
                    <motion.div
                      key={key}
                      whileHover={{ scale: 1.02 }}
                      className={`relative cursor-pointer`}
                    >
                      <Card className={`transition-all ${selectedPackage === key ? 'ring-2 ring-primary' : ''}`}>
                        <CardHeader className="pb-2">
                          <div className="flex items-start justify-between">
                            <div className="flex items-center gap-3">
                              <RadioGroupItem value={key} id={key} />
                              <div>
                                <CardTitle className="text-lg">
                                  {isRTL ? pkg.name_ar : pkg.name_en}
                                </CardTitle>
                                <CardDescription>
                                  {isRTL ? pkg.description_ar : pkg.description_en}
                                </CardDescription>
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="text-2xl font-bold text-primary">{pkg.price} SAR</div>
                              <div className="text-sm text-muted-foreground">
                                {isRTL ? "شهر واحد" : "1 month"}
                              </div>
                              {pkg.isLimited && (
                                <span className="inline-block mt-1 text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-medium">
                                  {isRTL ? "عرض لفترة محدودة!" : "Limited time!"}
                                </span>
                              )}
                            </div>
                          </div>
                        </CardHeader>
                        <CardContent>
                          <ul className="space-y-2">
                            {(isRTL ? pkg.features_ar : pkg.features_en).map((feature, i) => (
                              <li key={i} className="flex items-center gap-2 text-sm">
                                <CheckCircle2 className="w-4 h-4 text-green-500" />
                                {feature}
                              </li>
                            ))}
                          </ul>
                        </CardContent>
                      </Card>
                    </motion.div>
                  ))}
                </RadioGroup>

                {/* Preview */}
                {imagePreview && (
                  <div className="mt-8">
                    <h3 className="text-lg font-semibold mb-4">
                      {isRTL ? "معاينة الإعلان" : "Ad Preview"}
                    </h3>
                    <Card className="overflow-hidden">
                      <div className={`relative ${selectedPackage === 'banner' ? 'aspect-[4/1]' : 'aspect-[3/4]'}`}>
                        <img 
                          src={imagePreview} 
                          alt="Ad preview" 
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                        <div className="absolute bottom-4 left-4 right-4 text-white">
                          <h4 className="font-bold text-lg">{formData.title || (isRTL ? "عنوان الإعلان" : "Ad Title")}</h4>
                          <p className="text-sm opacity-90">{formData.description || (isRTL ? "وصف الإعلان" : "Ad description")}</p>
                        </div>
                      </div>
                    </Card>
                  </div>
                )}
              </div>

              {/* Form */}
              <div>
                <h2 className="text-2xl font-bold mb-6">
                  {isRTL ? "بيانات الإعلان" : "Ad Details"}
                </h2>
                
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Advertiser Info */}
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="advertiser_name">
                        {isRTL ? "اسم المعلن *" : "Advertiser Name *"}
                      </Label>
                      <Input
                        id="advertiser_name"
                        name="advertiser_name"
                        value={formData.advertiser_name}
                        onChange={handleInputChange}
                        placeholder={isRTL ? "اسم الشركة أو الشخص" : "Company or person name"}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="advertiser_email">
                        {isRTL ? "البريد الإلكتروني *" : "Email *"}
                      </Label>
                      <Input
                        id="advertiser_email"
                        name="advertiser_email"
                        type="email"
                        value={formData.advertiser_email}
                        onChange={handleInputChange}
                        placeholder="email@example.com"
                        required
                      />
                    </div>
                  </div>

                  {/* Contact Phone */}
                  <div className="space-y-2">
                    <Label htmlFor="advertiser_phone">
                      {isRTL ? "رقم التواصل" : "Contact Phone"}
                    </Label>
                    <Input
                      id="advertiser_phone"
                      name="advertiser_phone"
                      type="tel"
                      value={formData.advertiser_phone}
                      onChange={handleInputChange}
                      placeholder={isRTL ? "05xxxxxxxx" : "05xxxxxxxx"}
                      dir="ltr"
                    />
                    <p className="text-xs text-muted-foreground flex items-center gap-1">
                      <span className="inline-block w-2 h-2 bg-green-500 rounded-full"></span>
                      {isRTL ? "لا يظهر للعملاء - للإدارة فقط" : "Not visible to customers - Admin only"}
                    </p>
                  </div>

                  {/* Ad Titles */}
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="title">
                        {isRTL ? "عنوان الإعلان (English) *" : "Ad Title (English) *"}
                      </Label>
                      <Input
                        id="title"
                        name="title"
                        value={formData.title}
                        onChange={handleInputChange}
                        placeholder="Your ad title"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="title_ar">
                        {isRTL ? "عنوان الإعلان (عربي)" : "Ad Title (Arabic)"}
                      </Label>
                      <Input
                        id="title_ar"
                        name="title_ar"
                        value={formData.title_ar}
                        onChange={handleInputChange}
                        placeholder="عنوان الإعلان"
                      />
                    </div>
                  </div>

                  {/* Descriptions */}
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="description">
                        {isRTL ? "الوصف (English)" : "Description (English)"}
                      </Label>
                      <Textarea
                        id="description"
                        name="description"
                        value={formData.description}
                        onChange={handleInputChange}
                        placeholder="Brief description"
                        rows={3}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="description_ar">
                        {isRTL ? "الوصف (عربي)" : "Description (Arabic)"}
                      </Label>
                      <Textarea
                        id="description_ar"
                        name="description_ar"
                        value={formData.description_ar}
                        onChange={handleInputChange}
                        placeholder="وصف مختصر"
                        rows={3}
                      />
                    </div>
                  </div>

                  {/* Link URL */}
                  <div className="space-y-2">
                    <Label htmlFor="link_url">
                      {isRTL ? "رابط الإعلان *" : "Ad Link URL *"}
                    </Label>
                    <Input
                      id="link_url"
                      name="link_url"
                      type="url"
                      value={formData.link_url}
                      onChange={handleInputChange}
                      placeholder="https://example.com"
                      required
                    />
                  </div>

                  {/* Image Upload */}
                  <div className="space-y-2">
                    <Label>
                      {isRTL ? "صورة الإعلان *" : "Ad Image *"}
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      {selectedPackage === 'banner'
                        ? (isRTL ? 'المقاس الموصى به: 1200×200 بكسل (بانر علوي)' : 'Recommended size: 1200×200 px (Top Banner)')
                        : (isRTL ? 'المقاس الموصى به: 300×250 بكسل (إعلان جانبي)' : 'Recommended size: 300×250 px (Sidebar)')
                      }
                    </p>
                    <div 
                      className="border-2 border-dashed rounded-lg p-8 text-center cursor-pointer hover:border-primary transition-colors"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      {imagePreview ? (
                        <div className="flex flex-col items-center gap-2">
                          <ImageIcon className="w-8 h-8 text-green-500" />
                          <span className="text-sm text-green-600">
                            {isRTL ? "تم رفع الصورة" : "Image uploaded"}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            {isRTL ? "انقر لتغيير الصورة" : "Click to change"}
                          </span>
                        </div>
                      ) : (
                        <div className="flex flex-col items-center gap-2">
                          <Upload className="w-8 h-8 text-muted-foreground" />
                          <span className="text-sm text-muted-foreground">
                            {isRTL ? "انقر لرفع الصورة" : "Click to upload image"}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            {isRTL ? "PNG, JPG (أقصى حجم 5MB)" : "PNG, JPG (Max 5MB)"}
                          </span>
                        </div>
                      )}
                    </div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handleImageUpload}
                    />
                    <p className="text-xs text-muted-foreground">
                      {selectedPackage === 'banner' 
                        ? (isRTL ? "الحجم الموصى به: 1200x300 بكسل" : "Recommended size: 1200x300 pixels")
                        : (isRTL ? "الحجم الموصى به: 300x400 بكسل" : "Recommended size: 300x400 pixels")
                      }
                    </p>
                  </div>

                  {/* Submit */}
                  <div className="pt-4">
                    <Button 
                      type="submit" 
                      size="lg" 
                      className="w-full"
                      disabled={isLoading}
                    >
                      {isLoading ? (
                        <>
                          <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                          {isRTL ? "جاري المعالجة..." : "Processing..."}
                        </>
                      ) : (
                        <>
                          <Megaphone className="w-5 h-5 mr-2" />
                          {isRTL 
                            ? `ادفع ${pkg.price} ريال واحصل على إعلانك`
                            : `Pay ${pkg.price} SAR & Get Your Ad`
                          }
                        </>
                      )}
                    </Button>
                    <p className="text-center text-xs text-muted-foreground mt-3">
                      {isRTL 
                        ? "سيتم مراجعة إعلانك من قبل فريقنا قبل النشر"
                        : "Your ad will be reviewed by our team before publishing"
                      }
                    </p>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}
